import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { trpc } from '@/lib/trpc';

// At-a-glance KPIs above the alert feed. Refetches every 30s — these are
// counts, not live ticks, so 30s is plenty (the dashboard's own polling
// drives the live feel of the cards below).

interface KpiProps {
  label: string;
  value: string | number;
  hint?: string;
}

function Kpi({ label, value, hint }: KpiProps) {
  return (
    <Card className="px-4 py-3">
      <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 font-mono text-2xl font-semibold text-foreground">{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground">{hint}</div>}
    </Card>
  );
}

export function MetricsStrip() {
  const metrics = trpc.system.metrics.useQuery(undefined, {
    refetchInterval: 30_000,
    staleTime: 30_000,
  });

  // Skeletons only on the very first load (no data yet). Once the cache is
  // populated, render the real cards continuously even during background
  // refetches — checking !metrics.data instead of isLoading is the simplest
  // way to avoid the "skeleton flashes every refresh" bug reported by ops.
  if (!metrics.data) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Skeleton className="h-20" />
        <Skeleton className="h-20" />
        <Skeleton className="h-20" />
        <Skeleton className="h-20" />
      </div>
    );
  }
  const m = metrics.data;
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <Kpi
        label="Activas ahora"
        value={m.opportunitiesActiveNow}
        hint={`${m.signalsActive} picks vigentes`}
      />
      <Kpi label="Detectadas hoy" value={m.opportunitiesToday} />
      <Kpi label="Partidos en radar" value={m.matchesInRadar} hint="±7 días" />
      <Kpi
        label="Última alerta"
        value={m.lastOpportunityAt ? formatRelativeTime(m.lastOpportunityAt) : '—'}
      />
    </div>
  );
}
